import { Controller } from "@hotwired/stimulus";

// Acquisition estimator: what needs to be true to hit the revenue goal.
// Dumb math, user assumptions only. Live updates on input change.
export default class extends Controller {
  static values = {
    goalCents: Number,
    currentCents: Number,
    projectedEomCents: Number,
    daysElapsed: Number,
    daysInMonth: Number
  };

  connect() {
    this.handleUpsellToggle();
    this.calculate();
  }

  calculate() {
    const goalCents = this.hasGoalCentsValue ? this.goalCentsValue : 0;
    const downPayment = this.parseMoney(this.getInputValue("downPayment")) || 0;
    const recurringAmount = this.parseMoney(this.getInputValue("recurringAmount")) || 0;
    const cyclesRemaining = this.computeCyclesRemaining();
    const bookRate = this.parsePercent(this.getInputValue("bookRate")) || 80;
    const showRate = this.parsePercent(this.getInputValue("showRate")) || 70;
    const closeRate = this.parsePercent(this.getInputValue("closeRate")) || 60;
    const costPerLead = this.parseMoney(this.getInputValue("costPerLead")) || 0;
    const upsellCheckbox = this.element.querySelector('input[type="checkbox"][name="acquisition_estimator[upsell_enabled]"]');
    const upsellEnabled = upsellCheckbox?.checked ?? false;
    const upsellAmount = this.parseMoney(this.getInputValue("upsellAmount")) || 0;
    const upsellTakeRate = this.parsePercent(this.getInputValue("upsellTakeRate")) || 20;

    const currentCents = this.hasCurrentCentsValue ? this.currentCentsValue : 0;
    const projectedEomCents = this.hasProjectedEomCentsValue ? this.projectedEomCentsValue : 0;
    const currentDollars = currentCents / 100;
    const revenueGapCents = Math.max(0, goalCents - projectedEomCents);
    const revenueGapDollars = revenueGapCents / 100;

    const upsellValue = upsellEnabled ? (upsellAmount * upsellTakeRate / 100) : 0;
    const revenuePerSale = downPayment + (recurringAmount * cyclesRemaining) + upsellValue;

    const hasGoal = goalCents > 0;
    const hasGap = revenueGapCents > 0;

    let salesNeeded = 0;
    let showsNeeded = 0;
    let bookingsNeeded = 0;
    let leadsNeeded = 0;
    let leadsPerDay = null;
    let adSpendNeeded = null;
    let adSpendPerDay = null;

    if (hasGap && revenuePerSale > 0 && closeRate > 0 && showRate > 0 && bookRate > 0) {
      salesNeeded = Math.ceil(revenueGapDollars / revenuePerSale);
      showsNeeded = Math.ceil(salesNeeded / (closeRate / 100));
      bookingsNeeded = Math.ceil(showsNeeded / (showRate / 100));
      leadsNeeded = Math.ceil(bookingsNeeded / (bookRate / 100));

      const daysRemaining = this.hasDaysInMonthValue && this.hasDaysElapsedValue
        ? Math.max(0, this.daysInMonthValue - this.daysElapsedValue)
        : 0;
      if (daysRemaining > 0) {
        leadsPerDay = Math.ceil(leadsNeeded / daysRemaining);
      }
      const cpl = costPerLead || 0;
      adSpendNeeded = leadsNeeded * cpl;
      adSpendPerDay = leadsPerDay != null ? leadsPerDay * cpl : null;
    }

    const { basedOn } = this.buildRevenuePerSaleBasedOn({
      downPayment,
      recurringAmount,
      cyclesRemaining,
      upsellValue,
      upsellEnabled
    });

    this.updateOutputs({
      hasGoal,
      hasGap,
      currentDollars,
      revenueGapDollars,
      revenueGapCents,
      revenuePerSale,
      revenuePerSaleBasedOn: basedOn,
      upsellValue,
      upsellEnabled,
      salesNeeded,
      showsNeeded,
      bookingsNeeded,
      leadsNeeded,
      leadsPerDay,
      adSpendNeeded,
      adSpendPerDay,
      closeRate,
      showRate,
      bookRate,
      daysRemaining: this.hasDaysInMonthValue && this.hasDaysElapsedValue
        ? Math.max(0, this.daysInMonthValue - this.daysElapsedValue)
        : null
    });
  }

  computeCyclesRemaining() {
    const daysRemaining = this.hasDaysInMonthValue && this.hasDaysElapsedValue
      ? Math.max(0, this.daysInMonthValue - this.daysElapsedValue)
      : 0;
    if (daysRemaining <= 0) return 0;

    const everyNumber = parseInt(this.getInputValue("everyNumber"), 10) || 1;
    const everyUnit = (this.getInputValue("everyUnit") || "week").toLowerCase();

    const unitDays = { day: 1, week: 7, month: 30 }[everyUnit] ?? 7;
    const intervalDays = everyNumber * unitDays;
    if (intervalDays <= 0) return 0;

    return Math.floor(daysRemaining / intervalDays);
  }

  buildRevenuePerSaleBasedOn({ downPayment, recurringAmount, cyclesRemaining, upsellValue, upsellEnabled }) {
    const parts = [];
    parts.push(`${this.formatCurrency(downPayment)} down`);
    if (cyclesRemaining > 0 && recurringAmount > 0) {
      const unit = (this.getInputValue("everyUnit") || "week").toLowerCase();
      const unitLabel = unit === "day" ? "daily" : unit === "week" ? "weekly" : "monthly";
      parts.push(`${cyclesRemaining} ${unitLabel} payment${cyclesRemaining !== 1 ? "s" : ""} before month end`);
    }
    if (upsellEnabled) {
      parts.push(`${this.formatCurrency(upsellValue)} upsell`);
    }
    const basedOn = parts.length > 0 ? `Based on: ${parts.join(" + ")}` : "";
    return { basedOn };
  }

  getInputValue(targetName) {
    const el = this.element.querySelector(`[data-acquisition-estimator-target="${targetName}"]`);
    const input = el?.tagName === "INPUT" || el?.tagName === "SELECT" ? el : el?.querySelector?.("input, select");
    return (input || el)?.value?.trim() ?? "";
  }

  parseMoney(val) {
    if (!val) return null;
    const cleaned = val.replace(/[,$\s]/g, "");
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  parsePercent(val) {
    if (!val) return null;
    const n = parseFloat(val.replace(/[%\s]/g, ""));
    return isNaN(n) ? null : n;
  }

  formatCurrency(dollars) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(dollars);
  }

  formatCurrencyWithCents(dollars) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  }

  updateOutputs(data) {
    this.setOutput("currentRevenue", data.currentDollars, this.formatCurrency);
    this.setOutput("revenueGap", data.revenueGapDollars, this.formatCurrencyWithCents);

    const onPaceEl = this.element.querySelector('[data-acquisition-estimator-target="onPaceMessage"]');
    if (onPaceEl) {
      onPaceEl.classList.toggle("hidden", data.revenueGapCents > 0);
    }
    this.setOutput("revenuePerSale", data.revenuePerSale, this.formatCurrency);
    const upsellNoteEl = this.element.querySelector('[data-acquisition-estimator-target="revenuePerSaleUpsellNote"]');
    if (upsellNoteEl) {
      upsellNoteEl.classList.toggle("hidden", !data.upsellEnabled);
    }
    this.setOutput("upsellValueSummary", data.upsellValue, this.formatCurrency);
    const basedOnEl = this.element.querySelector('[data-acquisition-estimator-target="revenuePerSaleBasedOn"]');
    if (basedOnEl) {
      basedOnEl.textContent = data.revenuePerSaleBasedOn || "";
      basedOnEl.classList.toggle("hidden", !data.revenuePerSaleBasedOn);
    }
    this.setOutput("upsellValue", data.upsellValue, this.formatCurrency);
    this.setOutput("salesNeeded", data.salesNeeded, (n) => n.toLocaleString());
    this.setOutput("showsNeeded", data.showsNeeded, (n) => n.toLocaleString());
    this.setOutput("bookingsNeeded", data.bookingsNeeded, (n) => n.toLocaleString());
    this.setOutput("leadsNeeded", data.leadsNeeded, (n) => n.toLocaleString());
    this.setOutput("leadsPerDay", data.leadsPerDay, (n) => n.toLocaleString());
    this.setOutput("adSpendNeeded", data.adSpendNeeded, this.formatCurrency);
    this.setOutput("adSpendPerDay", data.adSpendPerDay, this.formatCurrency);

    this.setOutput("closeRateLabel", `Close Rate ${Math.round(data.closeRate || 0)}%`, null);
    this.setOutput("showRateLabel", `Show Rate ${Math.round(data.showRate || 0)}%`, null);
    this.setOutput("bookRateLabel", `Book Rate ${Math.round(data.bookRate || 0)}%`, null);

    const emptySection = this.element.querySelector('[data-acquisition-estimator-target="emptyState"]');
    const mainSection = this.element.querySelector('[data-acquisition-estimator-target="mainContent"]');
    const catchUpSection = this.element.querySelector('[data-acquisition-estimator-target="catchUpSection"]');

    if (emptySection && mainSection) {
      if (data.hasGoal) {
        emptySection.classList.add("hidden");
        mainSection.classList.remove("hidden");
      } else {
        emptySection.classList.remove("hidden");
        mainSection.classList.add("hidden");
      }
    }

    if (catchUpSection) {
      catchUpSection.classList.toggle("hidden", !data.hasGap);
    }
  }

  setOutput(targetName, value, formatter) {
    const els = this.element.querySelectorAll(`[data-acquisition-estimator-target="${targetName}"]`);
    const display = value == null || (typeof value === "number" && isNaN(value))
      ? "—"
      : (formatter ? formatter(value) : String(value));
    els.forEach((el) => { el.textContent = display; });
  }

  handleInput() {
    this.calculate();
  }

  handleUpsellToggle() {
    const upsellFields = this.element.querySelector('[data-acquisition-estimator-target="upsellFields"]');
    const toggle = this.element.querySelector('input[type="checkbox"][name="acquisition_estimator[upsell_enabled]"]');
    if (upsellFields && toggle) {
      upsellFields.classList.toggle("hidden", !toggle.checked);
    }
    this.calculate();
  }
}
